import { useState, useEffect, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Github, ExternalLink, FileCode, Folder, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Attempt to dynamically import an app component
const tryLoadApp = (appId) => {
    // Convert app-id to PascalCase for component name
    const pascalCase = appId.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
    ).join('');
    
    return lazy(() => 
        import(`../${appId}/${pascalCase}App.js`).catch(() => {
            // If import fails, return a placeholder component
            return { default: null };
        })
    );
};

export default function GenericApp() {
    const { appId } = useParams();
    const [app, setApp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [DynamicApp, setDynamicApp] = useState(null);
    const [loadingDynamic, setLoadingDynamic] = useState(true);

    useEffect(() => {
        fetchApp();
        attemptDynamicLoad();
    }, [appId]);

    const fetchApp = async () => {
        try {
            const response = await axios.get(`${API_URL}/apps/${appId}`);
            setApp(response.data);
        } catch (err) {
            console.error('Failed to fetch app:', err);
        } finally {
            setLoading(false);
        }
    };

    const attemptDynamicLoad = async () => {
        setLoadingDynamic(true);
        try {
            const LazyComponent = tryLoadApp(appId);
            setDynamicApp(() => LazyComponent);
        } catch (err) {
            console.log('Could not dynamically load app:', appId);
            setDynamicApp(null);
        } finally {
            setLoadingDynamic(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
                <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
        );
    }

    if (!app) {
        return (
            <div className="p-8 text-center">
                <h2 className="text-xl font-semibold text-slate-900">App not found</h2>
                <p className="text-slate-500 mt-2">This app may have been removed or doesn't exist.</p>
            </div>
        );
    }

    // Try to render the dynamically loaded app
    if (DynamicApp && !loadingDynamic) {
        return (
            <Suspense fallback={
                <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
                    <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                </div>
            }>
                <DynamicAppWrapper Component={DynamicApp} fallback={
                    <GenericAppPlaceholder app={app} appId={appId} />
                } />
            </Suspense>
        );
    }

    return <GenericAppPlaceholder app={app} appId={appId} />;
}

// Wrapper to handle dynamic app loading with error boundary
function DynamicAppWrapper({ Component, fallback }) {
    const [hasError, setHasError] = useState(false);
    const [LoadedComponent, setLoadedComponent] = useState(null);

    useEffect(() => {
        // Try to resolve the lazy component
        if (Component) {
            try {
                setLoadedComponent(() => Component);
            } catch {
                setHasError(true);
            }
        }
    }, [Component]);

    if (hasError || !LoadedComponent) {
        return fallback;
    }

    return (
        <Suspense fallback={
            <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
                <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
        }>
            <ErrorBoundary fallback={fallback}>
                <LoadedComponent />
            </ErrorBoundary>
        </Suspense>
    );
}

// Simple error boundary
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback;
        }
        return this.props.children;
    }
}

// Import React for error boundary
import React from 'react';

// Placeholder component for apps that haven't been integrated
function GenericAppPlaceholder({ app, appId }) {
    return (
        <div className="p-8" data-testid={`app-${appId}`}>
            {/* App Header */}
            <div className="mb-8">
                <div className="flex items-center gap-4">
                    <div 
                        className="w-14 h-14 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${app.color}20` }}
                    >
                        <FileCode className="w-7 h-7" style={{ color: app.color }} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                            {app.name}
                        </h1>
                        <p className="text-slate-500">{app.description}</p>
                    </div>
                </div>
            </div>

            {/* App Info */}
            <div className="flex items-center gap-3">
                <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                    app.status === 'live'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-blue-50 text-blue-700 border border-blue-200'
                }`}>
                    {app.status}
                </span>
                <code className="text-xs font-mono text-slate-400">{app.id}</code>
                {app.github_url && (
                    <a
                        href={app.github_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700"
                    >
                        <Github className="w-3.5 h-3.5" />
                        GitHub
                    </a>
                )}
            </div>

            {/* Refresh Notice */}
            <Card className="mt-6 border-amber-200 bg-amber-50">
                <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center border border-amber-200 flex-shrink-0">
                            <RefreshCw className="w-6 h-6 text-amber-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-amber-900 mb-1">Refresh Required</h3>
                            <p className="text-sm text-amber-700 leading-relaxed mb-3">
                                This app was just created. Please refresh the page to load the app's interface.
                            </p>
                            <Button 
                                onClick={() => window.location.reload()}
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Refresh Page
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
